"use client";

import { useState } from "react";
import { SpecOptionPills } from "@/components/listing/FurnitureOptionalDimensionsFields";
import {
  GAS_COOKER_BURNER_COUNT_MAX,
  GAS_COOKER_BURNER_COUNT_MIN,
  GAS_COOKER_MOUNT_TYPES,
  type GasCookerMountType,
  type GasCookerSpecFormState,
} from "@/lib/listing/gasCookerListingSpec";
import { cn } from "@/lib/utils";

interface GasCookerSpecFieldsProps {
  value: GasCookerSpecFormState;
  onChange: (next: GasCookerSpecFormState) => void;
  className?: string;
}

const CYLINDER_CHOICES = ["yes", "no"] as const;
type CylinderChoice = (typeof CYLINDER_CHOICES)[number];

function formatMountLabel(value: GasCookerMountType): string {
  return value === "table_top" ? "Table-top" : "Standing";
}

function formatCylinderLabel(value: CylinderChoice): string {
  return value === "yes" ? "Yes" : "No";
}

function cylinderChoiceFromForm(
  included: GasCookerSpecFormState["gasCylinderIncluded"],
): CylinderChoice | "" {
  if (included === true) return "yes";
  if (included === false) return "no";
  return "";
}

/**
 * Step 2 panel for canonical `gas-cooker` (Wave 2 gas cooker unified Phase 2).
 * Mount + cylinder required; burner count optional (collapsed).
 */
export default function GasCookerSpecFields({
  value,
  onChange,
  className = "",
}: GasCookerSpecFieldsProps) {
  const hasBurners =
    typeof value.burnerCount === "number" && Number.isFinite(value.burnerCount);
  const [detailsOpen, setDetailsOpen] = useState(hasBurners);

  return (
    <div className={cn("space-y-6", className)}>
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Mount type <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...GAS_COOKER_MOUNT_TYPES]}
          selected={value.gasCookerMountType}
          onSelect={(gasCookerMountType: GasCookerMountType) =>
            onChange({ ...value, gasCookerMountType })
          }
          formatLabel={formatMountLabel}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Includes gas cylinder? <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...CYLINDER_CHOICES]}
          selected={cylinderChoiceFromForm(value.gasCylinderIncluded)}
          onSelect={(choice: CylinderChoice) =>
            onChange({
              ...value,
              gasCylinderIncluded: choice === "yes",
            })
          }
          formatLabel={formatCylinderLabel}
        />
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setDetailsOpen((prev) => !prev)}
          className="flex w-full items-center justify-between rounded-lg border border-border bg-surface px-3 py-2 text-left text-sm font-medium text-foreground hover:border-border-strong"
          aria-expanded={detailsOpen}
        >
          <span>Add details — optional</span>
          <span className="text-xs font-normal text-text-light">
            {detailsOpen ? "Hide" : "Add"}
          </span>
        </button>
        {detailsOpen ? (
          <div className="space-y-2 rounded-lg border border-border bg-secondary/40 p-3">
            <label className="block text-sm font-medium text-foreground">
              Number of burners
            </label>
            <input
              type="number"
              inputMode="numeric"
              min={GAS_COOKER_BURNER_COUNT_MIN}
              max={GAS_COOKER_BURNER_COUNT_MAX}
              step={1}
              value={value.burnerCount === "" ? "" : value.burnerCount}
              onChange={(e) => {
                const raw = e.target.value.trim();
                if (!raw) {
                  onChange({ ...value, burnerCount: "" });
                  return;
                }
                const n = Number.parseInt(raw, 10);
                onChange({
                  ...value,
                  burnerCount: Number.isFinite(n) ? n : "",
                });
              }}
              placeholder="e.g. 4"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground"
            />
            <p className="text-xs text-text-light">
              Optional — skip if you don&apos;t know the count.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
